import { IconButton } from '@atlaskit/button/new';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { borderRadius } from '@atlaskit/theme';
import { N100 } from '@atlaskit/theme/colors';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import DropdownMenu, {
  type CustomTriggerProps,
  DropdownItem,
  DropdownItemGroup,
  type DropdownMenuProps,
  type OnOpenChangeArgs,
} from '@databases/atlaskit/DropdownMenu';
import { collabStateManager } from '@databases/collab-state-manager';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { doProcessPastedContent } from '@databases/copy-paste/actions';
import { useDisplayPasteFlags } from '@databases/copy-paste/useDisplayPasteFlags';
import { usePasteListener } from '@databases/copy-paste/usePasteListener';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { useDatabaseContent } from '@databases/database-state';
import { useDialog } from '@databases/dialog';
import { pasteContentExperience } from '@databases/frontend-metrics';
import { getFieldTypeName } from '@databases/intl/field-type-names';
import { messages } from '@databases/intl/messages';
import { REDO_SHORTCUT, UNDO_SHORTCUT } from '@databases/keyboard-shortcuts/shortcuts';
import { useMentionNotification } from '@databases/mention-notification';
import { isStandalone } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import { useComponent, useKeyboardShortcut } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { CONFLUENCE_SUPPORT_URL } from '@databases/utils/constants';
import {
  type DatabaseBridgeAPI,
  getBridgeAPI,
  type Item,
} from '@databases/utils/database/bridge';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAsync } from 'react-async';
import { useIntl } from 'react-intl-next';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { getIdentity } from '@canvas/connection/identity';

import { isApple } from '../../utils';

export type DatabaseActionKey =
  | 'open'
  | 'lock'
  | 'import'
  | 'print'
  | 'help'
  | 'copy'
  | 'feedback';

const ToggleWrapper = styled.div`
  margin-left: ${token('space.negative.100')};
  margin-right: ${token('space.negative.100')};
`;

const Shortcut = styled.div`
  margin-left: ${token('space.negative.100')};
  background-color: ${token('color.background.neutral', '#091E420F')};
  color: ${token('color.text.subtle', N100)};
  border-radius: ${borderRadius()}px;
  padding: ${token('space.050', '4px')};
  font: ${token('font.body.small')};
  align-self: flex-end;
  @media (max-width: 0px) {
    display: none;
  }
`;

const doToggleLock =
  (contentId: string): ThunkAction<void, DefaultRootState, void, AnyAction> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), { contentId });
    dispatch(
      addChangeToDraft(contentId, 'main', {
        type: DatabaseChangeType.TOGGLE_LOCK_STRUCTURE,
        newValue: !database.meta.isLocked,
      })
    );
  };

export interface DatabaseActionsProps {
  contentId: string;
  isReadOnly: boolean;
  placement?: DropdownMenuProps['placement'];
  show?: DatabaseActionKey[];
}

const DatabaseActions: FunctionComponent<DatabaseActionsProps> = ({
  contentId,
  isReadOnly,
  placement,
  show,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const { view } = useView();
  const { openThread } = useCommentStateContext();
  const { showPanel } = usePanel();
  const { referencing } = useDatabaseContent();
  const { createMentionNotification } = useMentionNotification({ contentId });
  const collabManager = collabStateManager.getCollabManager(contentId);
  const undoManager = collabManager?.database.undoManager;
  const showFlags = useDisplayPasteFlags(undoManager);

  const frameContext = useSnapshot(currentHostFrameContext);
  const isExportDisabledByDSP = frameContext.data.dataSecurityPolicy?.pageExport !== true;

  const database = defaultStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdRequired(state, { contentId })
  );

  const [isOpen, setIsOpen] = useState(false);
  const [showDevMenu, setShowDevMenu] = useState(false);
  const { showDialog } = useDialog();

  useEffect(() => {
    const loadDevMenu = async () => {
      const email = (await getIdentity()).email;
      if (email?.endsWith('@atlassian.com') || email?.endsWith('@usersinbuckets.com')) {
        setShowDevMenu(true);
      }
    };

    loadDevMenu();
  }, []);

  const { formatMessage } = useIntl();

  const lockAsync = useAsync({
    deferFn: useCallback(async () => {
      await dispatch(doToggleLock(contentId));
    }, [contentId, dispatch]),
  });
  const handleLock = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!lockAsync.isPending) {
        lockAsync.run();
      }
    },
    [lockAsync]
  );

  const handleExportClick = useCallback(() => {
    showDialog('ExportDialog');
  }, [showDialog]);

  const handleSetupClick = useCallback(() => {
    showPanel('DatabaseSetupPanel');
  }, [showPanel]);

  const handleCopyClick = useCallback(() => {
    showPanel('CopyDatabasePanel');
  }, [showPanel]);

  const handleUndo = useCallback(() => {
    if (openThread.valueId) {
      return;
    }
    undoManager?.undo();
  }, [undoManager, openThread.valueId]);
  useKeyboardShortcut(UNDO_SHORTCUT, handleUndo);

  const handleRedo = useCallback(() => {
    if (openThread.valueId) {
      return;
    }
    undoManager?.redo();
  }, [undoManager, openThread.valueId]);
  useKeyboardShortcut(REDO_SHORTCUT, handleRedo);

  const handleOpenDevTools = useCallback(() => {
    showPanel('DevtoolsPanel');
  }, [showPanel]);

  const handleOpenChange = useCallback((attrs: OnOpenChangeArgs) => {
    setIsOpen(attrs.isOpen);
  }, []);

  const trigger = useComponent(
    ({ triggerRef, ...props }: CustomTriggerProps) => (
      <IconButton
        testId="actions-menu.more-actions"
        icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="medium" />}
        label={formatMessage(messages['actions-menu.more-actions'])}
        appearance="subtle"
        ref={triggerRef as React.Ref<HTMLButtonElement>}
        {...props}
      />
    ),
    [formatMessage]
  );

  const actions = useMemo(
    () => ({
      open: !show || show.includes('open'),
      lock: (!show || show.includes('lock')) && database.operations.updateMetadata,
      import: (!show || show.includes('import')) && database.operations.updateEntries,
      // used for export
      print: (!show || show.includes('print')) && !isExportDisabledByDSP,
      help: !show || show.includes('help'),
      copy: !show || show.includes('copy'),
      feedback: !show || show.includes('feedback'),
    }),
    [
      show,
      database.operations.updateMetadata,
      database.operations.updateEntries,
      isExportDisabledByDSP,
    ]
  );

  const handlePaste = useCallback(
    (items: DataTransferItemList) => {
      if (openThread.valueId) {
        return;
      }

      if (isReadOnly) {
        showFlags([
          {
            actionType: 'readOnly',
            type: 'warning',
          },
        ]);

        return;
      }

      pasteContentExperience.start();

      client.sendContentPastedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
      });

      const fieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldTypeName(DatabaseFieldType.TEXT)),
        }
      );

      dispatch(
        doProcessPastedContent(
          contentId,
          items,
          view.id,
          fieldLabel,
          showFlags,
          createMentionNotification,
          view.settings
        )
      );
    },
    [
      openThread.valueId,
      isReadOnly,
      client,
      contentId,
      formatMessage,
      dispatch,
      view.id,
      view.settings,
      showFlags,
      createMentionNotification,
    ]
  );

  usePasteListener({
    onPaste: handlePaste,
  });

  const canUndo = !undoManager?.canUndo();
  const canRedo = !undoManager?.canRedo();

  const [actionsMovedToMoreMenu, setActionsMovedToMoreMenu] = useState(false);

  const [bridgeApi, setBridgeApi] = useState<any | DatabaseBridgeAPI>(null);
  useEffect(() => {
    const loadFromBridgeAPI = async () => {
      await getBridgeAPI().then(setBridgeApi);
    };
    loadFromBridgeAPI();
  }, []);

  useEffect(() => {
    if (!isStandalone() || !bridgeApi) {
      return;
    }

    const undoAndRedo: Item[] = [
      {
        id: 'databases-undo',
        text: formatMessage(messages['actions-menu.undo']),
        target: '_blank',
        isDisabled: canUndo,
        shortcutText: isApple
          ? formatMessage(messages['actions-menu.mac-undo-shortcut'])
          : formatMessage(messages['actions-menu.non-mac-undo-shortcut']),
        onClick: handleUndo,
      },
      {
        id: 'databases-redo',
        text: formatMessage(messages['actions-menu.redo']),
        target: '_blank',
        isDisabled: canRedo,
        shortcutText: isApple
          ? formatMessage(messages['actions-menu.mac-redo-shortcut'])
          : formatMessage(messages['actions-menu.non-mac-redo-shortcut']),
        onClick: handleRedo,
      },
    ];

    const otherActions = [
      actions.lock && {
        id: 'databases-lock-structure',
        text: formatMessage(messages['actions-menu.lock-structure']),
        onClick: handleLock,
        showToggle: {
          isChecked: lockAsync.isPending
            ? !database.meta.isLocked
            : database.meta.isLocked,
          isDisabled: lockAsync.isPending,
        },
      },
      actions.copy && {
        id: 'databases-copy-structure',
        text: formatMessage(messages['actions-menu.duplicate-structure']),
        onClick: handleCopyClick,
      },
      !referencing &&
        !isReadOnly && {
          id: 'databases-templates',
          text: formatMessage(messages['actions-menu.import-and-templates']),
          onClick: handleSetupClick,
        },
      actions.print && {
        id: 'databases-export',
        text: formatMessage(messages['actions-menu.export']),
        onClick: handleExportClick,
      },
      { id: 'view-resolved-comments' },
    ].filter(Boolean) as Item[];

    const getHelp: Item[] = [
      {
        id: 'databases-get-help',
        text: formatMessage(messages['actions-menu.get-help']),
        url: CONFLUENCE_SUPPORT_URL,
        target: '_blank',
      },
      showDevMenu && {
        id: 'databases-devtools',
        text: formatMessage(messages['actions-menu.devtools']),
        onClick: handleOpenDevTools,
      },
    ].filter(Boolean) as Item[];

    const items: Item[][] = [];

    if (undoManager) {
      items.push(undoAndRedo);
    }
    if (otherActions.length) {
      items.push(otherActions);
    }
    if (actions.help) {
      items.push(getHelp);
    }

    bridgeApi.updateMoreActions?.({
      items,
    });

    setActionsMovedToMoreMenu(true);
  }, [
    actions,
    canRedo,
    canUndo,
    database.meta.isLocked,
    formatMessage,
    handleCopyClick,
    handleExportClick,
    handleLock,
    handleOpenDevTools,
    handleRedo,
    handleSetupClick,
    handleUndo,
    isReadOnly,
    lockAsync.isPending,
    referencing,
    showDevMenu,
    undoManager,
    bridgeApi,
  ]);

  if (isAIFCStagingAreaView() && showDevMenu) {
    return (
      <DropdownMenu
        trigger={trigger}
        placement={placement}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DropdownItemGroup>
          <DropdownItem onClick={handleOpenDevTools}>
            {formatMessage(messages['actions-menu.devtools'])}
          </DropdownItem>
        </DropdownItemGroup>
      </DropdownMenu>
    );
  }

  const hasActions = Object.values(actions).filter((a) => a).length > 0;

  if (!hasActions || actionsMovedToMoreMenu) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        trigger={trigger}
        placement={placement}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DropdownItemGroup>
          {undoManager && (
            <DropdownItem
              elemAfter={
                <Shortcut>
                  {isApple
                    ? formatMessage(messages['actions-menu.mac-undo-shortcut'])
                    : formatMessage(messages['actions-menu.non-mac-undo-shortcut'])}
                </Shortcut>
              }
              onClick={handleUndo}
              isDisabled={!undoManager.canUndo()}
            >
              {formatMessage(messages['actions-menu.undo'])}
            </DropdownItem>
          )}

          {undoManager && (
            <DropdownItem
              elemAfter={
                <Shortcut>
                  {isApple
                    ? formatMessage(messages['actions-menu.mac-redo-shortcut'])
                    : formatMessage(messages['actions-menu.non-mac-redo-shortcut'])}
                </Shortcut>
              }
              onClick={handleRedo}
              isDisabled={!undoManager.canRedo()}
            >
              {formatMessage(messages['actions-menu.redo'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>

        <DropdownItemGroup>
          {actions.lock && (
            <Tooltip
              content={formatMessage(messages['actions-menu.lock-structure-tooltip'])}
            >
              <DropdownItem
                elemAfter={
                  <ToggleWrapper>
                    <Toggle
                      isChecked={
                        lockAsync.isPending
                          ? !database.meta.isLocked
                          : database.meta.isLocked
                      }
                      isDisabled={lockAsync.isPending}
                    />
                  </ToggleWrapper>
                }
                onClick={handleLock}
              >
                {formatMessage(messages['actions-menu.lock-structure'])}
              </DropdownItem>
            </Tooltip>
          )}
          {actions.copy && (
            <DropdownItem onClick={handleCopyClick}>
              {formatMessage(messages['actions-menu.duplicate-structure'])}
            </DropdownItem>
          )}

          {!referencing && (
            <DropdownItem
              testId="actions-menu.import-and-templates"
              onClick={handleSetupClick}
            >
              {formatMessage(messages['actions-menu.import-and-templates'])}
            </DropdownItem>
          )}

          {actions.print && (
            <DropdownItem onClick={handleExportClick}>
              {formatMessage(messages['actions-menu.export'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>

        <DropdownItemGroup>
          {actions.help && (
            <DropdownItem
              href={CONFLUENCE_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatMessage(messages['actions-menu.get-help'])}
            </DropdownItem>
          )}

          {showDevMenu && (
            <DropdownItem onClick={handleOpenDevTools}>
              {formatMessage(messages['actions-menu.devtools'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>
      </DropdownMenu>
    </>
  );
};

export default DatabaseActions;
